import { differenceInCalendarDays } from 'date-fns'
import type { CareActionKind } from '@/types/interfaces/careActions'

const CALENDAR_DAY_UPCOMING_KINDS = new Set<CareActionKind>([
    'in_person_visit_order',
    'referral_care_action',
    'appointment',
])

const SCHEDULED_SLOT_END_UPCOMING_KINDS = new Set<CareActionKind>([
    'video_visit',
    'video_visit_with_specialist_consultation',
])

const UNSCHEDULED_UNTIL_TERMINAL_KINDS = new Set<CareActionKind>([
    'in_person_visit_order',
    'referral_care_action',
])

const CARE_UPCOMING_TERMINAL_STATES = new Set([
    'completed',
    'complete',
    'resulted',
    'canceled',
    'cancelled',
    'abandoned',
    'hold_expired',
    'request_canceled',
    'visit_not_needed',
    'patient_no_show',
    'encounter_deleted',
    'replaced',
    'sent',
    'send_complete',
    'first_send_complete',
])

function parseTime(raw?: string | null): number | null {
    if (!raw) return null
    const time = new Date(raw).getTime()
    return Number.isNaN(time) ? null : time
}

/** True while `now` is still on the local calendar day of `scheduledAt` or earlier. */
export function isUpcomingThroughScheduledCalendarDay(
    scheduledAt: string | null | undefined,
    now: number = Date.now(),
): boolean {
    const scheduled = parseTime(scheduledAt)
    if (scheduled === null) return false
    return differenceInCalendarDays(now, scheduled) <= 0
}

export function usesCalendarDayUpcomingBoundary(kind: CareActionKind): boolean {
    return CALENDAR_DAY_UPCOMING_KINDS.has(kind)
}

export function usesScheduledSlotEndBoundary(kind: CareActionKind): boolean {
    return SCHEDULED_SLOT_END_UPCOMING_KINDS.has(kind)
}

export function visitUsesScheduledSlotEndBoundary(visitType: string): boolean {
    return visitType === 'Video visit' || visitType === SPECIALIST_CONSULTATION_VISIT_TYPE
}

export function staysUpcomingWhenUnscheduledUntilTerminal(kind: CareActionKind): boolean {
    return UNSCHEDULED_UNTIL_TERMINAL_KINDS.has(kind)
}

export function isCareUpcomingTerminalState(state?: string | null): boolean {
    return CARE_UPCOMING_TERMINAL_STATES.has((state ?? '').toLowerCase())
}

export const SPECIALIST_CONSULTATION_VISIT_TYPE = 'Video visit with specialist consultation'
export const IN_PERSON_VISIT_TYPE = 'InPersonVisit'
export const URGENT_CARE_VISIT_TYPE = 'UrgentCare'
